import { useCallback, useMemo, useState } from 'react'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { errorToString } from '@/lib/errors'
import { pb } from '@/lib/pocketbase'
import type { InviteDialogState } from '@/types/people'

export function useInviteDialog() {
    const { org: currentOrg } = useCurrentOrg()
    const [inviteDialog, setInviteDialog] = useState<InviteDialogState>({
        open: false,
        email: '',
        isLoading: false,
    })

    const openInviteDialog = useCallback(() => {
        setInviteDialog({
            open: true,
            email: '',
            isLoading: false,
        })
    }, [])

    const closeInviteDialog = useCallback(() => {
        setInviteDialog({
            open: false,
            email: '',
            isLoading: false,
        })
    }, [])

    const handleEmailChange = useCallback((email: string) => {
        setInviteDialog(prev => ({
            ...prev,
            email,
        }))
    }, [])

    const isInviteDisabled = useMemo(() => {
        return (
            !inviteDialog.email.trim() ||
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteDialog.email) ||
            inviteDialog.isLoading
        )
    }, [inviteDialog.email, inviteDialog.isLoading])

    const confirmInvite = useCallback(async () => {
        if (isInviteDisabled) return

        setInviteDialog(prev => ({ ...prev, isLoading: true }))

        try {
            await pb.collection('invitations').create({
                email: inviteDialog.email,
                name: inviteDialog.email.split('@')[0] || inviteDialog.email,
                role: 'member',
                org: currentOrg.id,
            })

            alert(`Invitation sent to ${inviteDialog.email}`)
            closeInviteDialog()
        } catch (error) {
            alert(`Failed to send invite: ${errorToString(error)}`)
        } finally {
            setInviteDialog(prev => ({ ...prev, isLoading: false }))
        }
    }, [closeInviteDialog, inviteDialog.email, isInviteDisabled, currentOrg.id])

    return {
        inviteDialog,
        openInviteDialog,
        closeInviteDialog,
        handleEmailChange,
        confirmInvite,
        isInviteDisabled,
    }
}
